import { ExternalProduct } from '../../../models/product/types';
import { TagProductAssociation } from '../../../redux/actions/tagActions/types';
import { BuildTagProductRowArgs, ProductType, TagProductRow } from './TagProductsTable.types';

export const columnToSortExpression: Record<string, string> = {
    name: 'Name',
    productCount: 'ProductCount',
    projectLibrary: 'ProjectLibrary',
    createdOn: 'CreatedOn',
    createdByName: 'CreatedByName',
};

export const sortExpressionRecord: Record<string, string> = {
    nameSort: columnToSortExpression.name,
    productCount: columnToSortExpression.productCount,
    projectLibraryText: columnToSortExpression.projectLibrary,
    dateTagAdded: columnToSortExpression.createdOn,
    addedBy: columnToSortExpression.createdByName,
};

export const toDateText = (value?: string) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString();
};

export const parseExternalProducts = (payloadData: any): ExternalProduct[] => {
    if (!payloadData?.mfSection) {
        return [];
    }
    const sections = payloadData.mfSection as Record<string, ExternalProduct[]>;
    return Object.keys(sections).reduce((agg, key) => {
        agg.push(...(sections[key] || []));
        return agg;
    }, [] as ExternalProduct[]);
};

export const getProjectLibraryMeta = (
    item: TagProductAssociation,
    organizationName?: string,
): { text: string; link: string } => {
    if (item.projectName) {
        return {
            text: item.projectName,
            link: item.projectId ? `/project-info/${item.projectId}/0/2` : '',
        };
    }
    if (item.isMyFavorite) {
        return { text: 'My Library', link: '/my-favorites' };
    }
    if (item.isOfficeFavorite) {
        return { text: `${organizationName || 'Office'} Library`, link: '/favorites' };
    }
    return { text: '', link: '' };
};

export const buildTagProductRow = ({
    item,
    externalById,
    companyById,
    organizationName,
}: BuildTagProductRowArgs): TagProductRow => {
    const productId = item.productId;
    const external = productId ? externalById[productId] : undefined;
    const manufacturer = external
        ? (companyById[external.companyID]?.company || external.company || '')
        : '';
    const productName = external?.productName || (productId ? `Product #${productId}` : '');
    const tags = item.tags || [];
    const tagsText = tags.map((t) => t.name).filter(Boolean).join(', ');

    // determine project/library display text and link via helper
    const { text: projectLibraryText, link: projectLibraryLink } = getProjectLibraryMeta(
        item,
        organizationName,
    );
    const productType = item.isMyFavorite || item.isOfficeFavorite ? ProductType.FavoriteProduct : ProductType.ProjectProduct;

    const catalogUrl = external?.transparencyCatalogUrl || external?.ec3Url || external?.listingUrl || external?.websiteUrl;

    return {
        id: productId!,
        key: item.id,
        manufacturer,
        productName,
        nameSort: manufacturer ? `${manufacturer} / ${productName}` : productName,
        tags,
        tagsSort: tagsText,
        productCount: 1,
        projectLibraryText,
        projectLibrarySort: projectLibraryText,
        projectLibraryLink,
        catalogUrl,
        dateTagAdded: item.createdAt,
        addedBy: item.createdByName || '',
        createdAtSortable: item.createdAt || '',
        productType,
    };
};
